
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePageContext } from "../hooks/usePageContext";
import { usePageFiltering } from "../hooks/usePageFiltering";
import { usePageRotation } from "../hooks/usePageRotation";
import { usePagePreloading } from "../hooks/usePagePreloading";
import { supabase } from "../integrations/supabase/client";
import DisplayContent from "../components/display/DisplayContent";
import MarqueeBarContainer from "../components/display/MarqueeBarContainer";
import DisplayHeader from "../components/display/DisplayHeader";
import DisplayNavigation from "../components/display/DisplayNavigation";
import { HomeTemplate } from "../components/templates/HomeTemplates";
import { useIsMobile } from "../hooks/use-mobile";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "@/components/ui/use-toast";
import { ArrowLeftRight, Home, Monitor, Settings } from "lucide-react";

const DisplayBoard: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const { user, editingUserId, isEditingAsDifferentUser } = useAuth();
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Track touch gestures
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [hasUsedSwipe, setHasUsedSwipe] = useState<boolean>(
    localStorage.getItem('hasUsedSwipe') === 'true'
  );

  // State to track whether mobile menu is open
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  
  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;
  
  // Check if a user parameter is specified (for super user editing)
  const userParam = searchParams.get('user');
  
  useEffect(() => {
    // If there's a user parameter and we're a super user, set editing mode
    if (userParam && !isEditingAsDifferentUser()) {
      // Store in localStorage that we're editing as this user
      localStorage.setItem('superUserEditingFor', userParam);
      // Force a refresh to apply the editing context
      window.location.reload();
    }
  }, [userParam, isEditingAsDifferentUser]);
  
  const {
    pages,
    isHebrew,
    autoScroll,
    titleConfig,
    currentUser,
    rotationInterval,
  } = usePageContext();

  // Use the page filtering hook to get organized pages
  const { enabledPages, leftPages, rightPages, fullscreenPages } = usePageFiltering();

  // State for fade animation
  const [fadeState, setFadeState] = useState<"fade-enter" | "fade-exit" | "">("");

  // Use the page rotation hook to manage page rotation
  const {
    currentLeftPage,
    currentRightPage,
    currentFullscreenPage,
    nextLeftPage,
    nextRightPage,
    nextFullscreenPage,
    goToPrevPage,
    goToNextPage,
    isTransitioning
  } = usePageRotation({
    leftPages,
    rightPages,
    fullscreenPages,
    autoScroll,
    rotationInterval,
    onPageChange: () => {
      setFadeState("fade-exit");
      setTimeout(() => setFadeState("fade-enter"), 500);
    }
  });

  // Use preloading hook to ensure pages are loaded before displaying
  const {
    visibleLeftPage,
    visibleRightPage,
    visibleFullscreenPage,
    isLoading
  } = usePagePreloading({
    leftPage: currentLeftPage,
    rightPage: currentRightPage,
    fullscreenPage: currentFullscreenPage,
    nextLeftPage,
    nextRightPage,
    nextFullscreenPage
  });

  // Calculate current page number and total for mobile indicator
  const getCurrentPageNumber = () => {
    if (currentFullscreenPage) {
      return fullscreenPages.findIndex(p => p.id === currentFullscreenPage.id) + 1;
    } else if (currentLeftPage && currentRightPage) {
      // In side-by-side mode, use the left page as the indicator
      return leftPages.findIndex(p => p.id === currentLeftPage.id) + 1;
    }
    return 1;
  };
  
  const getTotalPageCount = () => {
    if (currentFullscreenPage) {
      return fullscreenPages.length;
    } else {
      return Math.max(leftPages.length, rightPages.length);
    }
  };

  const generateDynamicTitle = (): React.ReactNode => {
    if (!titleConfig) return "ברוכים הבאים"; // Default fallback

    // Use the title from the TitleConfigEditor, fallback to currentPage.title with proper null checks
    if (titleConfig.text) {
      return titleConfig.text;
    }
    
    // Safely get the title from visible pages with proper undefined checks
    const currentPageTitle = visibleFullscreenPage?.title || 
                            visibleLeftPage?.title || 
                            visibleRightPage?.title;
    
    return currentPageTitle || "ברוכים הבאים";
  };

  // Supabase realtime subscription to reload on settings change
  useEffect(() => {
    try {
      // Listen to changes in app_settings and user_settings
      const channel = supabase.channel('realtime-refresh')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'app_settings' }, () => {
          console.log('App settings changed, reloading page...');
          window.location.reload();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'user_settings' }, () => {
          console.log('User settings changed, reloading page...');
          window.location.reload();
        })
        .subscribe();

      return () => {
        channel.unsubscribe();
      };
    } catch (error) {
      console.error('Error setting up realtime subscription in DisplayBoard page:', error);
      // Don't throw the error, just log it to prevent app from crashing
    }
  }, []);

  // Handle keyboard navigation for page changing
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        goToPrevPage();
      } else if (e.key === 'ArrowRight') {
        goToNextPage();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [goToPrevPage, goToNextPage]);
  
  // Touch event handlers for swipe navigation on mobile
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    // Handle swipe navigation based on Hebrew direction
    if (isLeftSwipe) {
      isHebrew ? goToPrevPage() : goToNextPage();
      setHasUsedSwipe(true);
      localStorage.setItem('hasUsedSwipe', 'true');
    }
    if (isRightSwipe) {
      isHebrew ? goToNextPage() : goToPrevPage();
      setHasUsedSwipe(true);
      localStorage.setItem('hasUsedSwipe', 'true');
    }
    
    // Reset values
    setTouchStart(null);
    setTouchEnd(null);
  };
  
  // Show a toast notification if we're in super user editing mode
  useEffect(() => {
    if (isEditingAsDifferentUser()) {
      toast({
        title: "Super User Editing Mode",
        description: `You are editing the dashboard for user ID: ${editingUserId?.substring(0, 8)}`,
      });
    }
  }, [isEditingAsDifferentUser, editingUserId]);

  // Show message when no pages are enabled
  if (enabledPages.length === 0) {
    return (
      <HomeTemplate titleConfig={titleConfig}>
        <div className="page-container items-center justify-center">
          <h1 className="heading-primary">אין דפים להצגה</h1>
          <p className="text-xl">אנא היכנסו לממשק הניהול כדי להוסיף דפים</p>
          {isEditingAsDifferentUser() && (
            <div className="mt-4">
              <p className="text-lg text-amber-600">
                You are in super user editing mode. This user has no pages.
              </p>
              <button
                onClick={() => navigate('/admin/super-dashboard')}
                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Return to Super User Dashboard
              </button>
            </div>
          )}
        </div>
      </HomeTemplate>
    );
  }

  // Log pages for debugging
  console.log("DisplayBoard rendering with pages:", {
    fullscreenPages: fullscreenPages.length,
    leftPages: leftPages.length,
    rightPages: rightPages.length,
    currentFullscreenPage: visibleFullscreenPage?.title,
    currentLeftPage: visibleLeftPage?.title,
    currentRightPage: visibleRightPage?.title,
    isLoading
  });

  return (
    <HomeTemplate titleConfig={titleConfig}>
      <div 
        className={`h-screen flex flex-col ${isHebrew ? "rtl" : "ltr"}`}
        style={
          titleConfig.background?.type === 'image' && titleConfig.background?.image?.url
            ? {
                backgroundImage: `url(${titleConfig.background.image.url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }
            : {}
        }
      >
        {/* Header */}
        <DisplayHeader
          titleConfig={titleConfig}
          isHebrewDefault={isHebrew}
          currentUser={currentUser}
          generateDynamicTitle={generateDynamicTitle}
          isEditingMode={isEditingAsDifferentUser()}
        />
        
        {/* Marquee Bar - Positioned at the top after header */}
        <div className="w-full shadow-lg">
          <MarqueeBarContainer />
        </div>
        
        {/* Mobile menu button */}
        {isMobile && (
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="fixed top-4 right-4 z-50 bg-white/40 backdrop-blur-sm p-2 rounded-full shadow-lg"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            )}
          </button>
        )}
        
        {/* Mobile menu overlay */}
        {isMobile && mobileMenuOpen && (
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setMobileMenuOpen(false)}></div>
        )}
        
        {/* Mobile menu */}
        {isMobile && (
          <div className={`fixed top-0 ${isHebrew ? 'right-0' : 'left-0'} bottom-0 w-64 bg-white/40 z-50 shadow-xl transition-transform duration-300 ease-in-out ${mobileMenuOpen ? 'translate-x-0' : isHebrew ? 'translate-x-full' : '-translate-x-full'}`}>
            <div className="p-4 bg-synagogue-blue text-white">
              <h2 className="text-xl font-bold">תפריט</h2>
            </div>
            <div className="p-4">
              <button 
                onClick={() => {
                  navigate('/dashboard');
                  setMobileMenuOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-right mb-2 hover:bg-gray-100 rounded"
              >
                <Home className="mr-2" size={18} />
                <span>לוח בקרה</span>
              </button>
              <button 
                onClick={() => {
                  goToPrevPage();
                  setMobileMenuOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-right mb-2 hover:bg-gray-100 rounded"
              >
                <ArrowLeftRight className="mr-2" size={18} />
                <span>דף קודם</span>
              </button>
              <button 
                onClick={() => {
                  goToNextPage();
                  setMobileMenuOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-right mb-2 hover:bg-gray-100 rounded"
              >
                <ArrowLeftRight className="mr-2" size={18} />
                <span>דף הבא</span>
              </button>
              <button 
                onClick={() => {
                  // Toggle screen mode - you can use document.documentElement.requestFullscreen() here
                  try {
                    if (document.fullscreenElement) {
                      document.exitFullscreen();
                    } else {
                      document.documentElement.requestFullscreen();
                    }
                  } catch (err) {
                    console.error("Error toggling fullscreen:", err);
                  }
                  setMobileMenuOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-right mb-2 hover:bg-gray-100 rounded"
              >
                <Monitor className="mr-2" size={18} />
                <span>מסך מלא</span>
              </button>
              <button 
                onClick={() => {
                  navigate('/dashboard/settings');
                  setMobileMenuOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-right hover:bg-gray-100 rounded"
              >
                <Settings className="mr-2" size={18} />
                <span>הגדרות</span>
              </button>
            </div>
          </div>
        )}

        <main 
          className="flex-1 relative overflow-y-auto scrollbar-custom" 
          ref={contentRef}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Navigation Controls - Better positioned for mobile */}
          <div className={`
            ${isMobile 
              ? 'fixed bottom-4 right-0 left-0 z-20 flex justify-center' 
              : 'absolute top-4 right-4 z-10'
            }`}
          >
            <DisplayNavigation 
              goToPrevPage={goToPrevPage} 
              goToNextPage={goToNextPage}
              isHebrew={isHebrew}
              isMobile={isMobile}
            />
          </div>

          {/* Mobile page indicator */}
          {isMobile && (
            <div className="fixed bottom-20 right-0 left-0 z-20 flex justify-center">
              <div className="bg-black/30 backdrop-blur-sm text-white rounded-full px-4 py-1 text-sm font-medium">
                {getCurrentPageNumber()} / {getTotalPageCount()}
              </div>
            </div>
          )}

          {/* Admin controls when in editing mode */}
          {isEditingAsDifferentUser() && (
            <div className="absolute top-4 left-4 z-10 bg-white/40 p-2 rounded shadow-lg border border-amber-500">
              <button
                onClick={() => navigate('/admin/super-dashboard')}
                className="px-3 py-1 bg-amber-500 text-white rounded hover:bg-amber-600 text-sm"
              >
                Exit Editing Mode
              </button>
            </div>
          )}

          {/* Swipe instruction for mobile users - only shown until first swipe */}
          {isMobile && !hasUsedSwipe && (
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none">
              <div className="text-center bg-black/50 backdrop-blur-sm text-white px-6 py-3 rounded-lg shadow-lg animate-pulse">
                <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-10 w-10 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 4h6v6"></path>
                  <path d="M10 4H4v6"></path>
                  <path d="M4 14h6v6"></path>
                  <path d="M14 14h6v6"></path>
                </svg>
                <p className="whitespace-nowrap">
                  {isHebrew ? 'החלק ימינה או שמאלה לדפדוף' : 'Swipe left or right to navigate'}
                </p>
              </div>
            </div>
          )}

          {/* Only render content when it's loaded - this creates seamless transitions */}
          <div className={`${isLoading ? 'opacity-0' : 'opacity-100 transition-opacity duration-300'} ${isMobile ? 'pt-8 pb-24' : ''}`}>
            <DisplayContent
              fadeState={fadeState}
              isHebrew={isHebrew}
              currentLeftPage={visibleLeftPage}
              currentRightPage={visibleRightPage}
              currentFullscreenPage={visibleFullscreenPage}
              isMobile={isMobile}
            />
          </div>
        </main>
      </div>
    </HomeTemplate>
  );
};

export default DisplayBoard;
